/**
 * `VenueAmenityGrid` — amenity card grid per the v2 design template
 * (`vn-amenities` block in the live `LOCI: Design System` project,
 * `templates/city/venue.html`, rebuilt 2026-09-06). 3-col, collapsing to
 * 2-col under 700px.
 *
 * WEB-861 — the v1 card printed three lines: a monospace caption
 * (`amenity.label`), the **raw token** (`amenity.value` — literally
 * `parking` / `wheelchair`), and `amenity.sub`. So a card read
 * "Parking / parking": machine vocabulary on a public page. v2 drops the
 * caption line entirely and promotes the human label to the value line, so
 * `amenity.value` is never rendered. The sub-line is reserved for a genuine
 * supplementary fact (only the Transit card has one today —
 * `~180 m to nearest stop`, derived from `walkingDistanceToTransit`) and
 * collapses when there is none.
 *
 * COR-59 — the section renders only what a source confirmed. `normalizeAmenities`
 * (backend) emits an entry for a `true` boolean and for each present
 * `amenities[]` token, and nothing at all for `false` or NULL. A missing
 * amenity is therefore *absent*, never a greyed-out "no" — an accessibility
 * claim, not a cosmetic one. When nothing is confirmed the section keeps its
 * frame and states that plainly rather than unmounting, so "unverified" never
 * reads as "unavailable".
 *
 * API-1709 — v2 adds a third line, the source attribution
 * (`.vn-amenity__src`): `TICKETMASTER · AUG 2026`. It is the counterpart to the
 * section footnote ("Only source-confirmed facts are listed") — the footnote
 * makes the promise, this line is the receipt. It renders ONLY for a fact the
 * API stamped, which today means the three boolean-derived cards; freeform
 * `amenities[]` tokens carry `source: null` and get no line at all rather than
 * a borrowed attribution.
 *
 * @since WEB-167
 */
'use client';

import * as React from 'react';
import { cn } from '@/components/atoms/primitives/utils';
import type { VenueAmenity, VenueAmenitySource } from '@/types/venueDetail';

export interface VenueAmenityGridViewProps {
  amenities: ReadonlyArray<VenueAmenity>;
  className?: string;
}

/**
 * A sub-line earns its slot only when it carries a real supplementary fact.
 * Null, undefined and whitespace-only values all collapse the slot rather
 * than rendering an empty row that pushes the card taller than its siblings.
 */
function hasSupplementaryFact(sub: string | null | undefined): sub is string {
  return typeof sub === 'string' && sub.trim().length > 0;
}

/**
 * Display name per provider (API-1709).
 *
 * An explicit map rather than a `replace(/_/g, ' ').toUpperCase()` transform:
 * the keys are a WIRE vocabulary, so a new lane must make a deliberate decision
 * about how it is credited on a public page. Typing it as a total `Record` over
 * the union is what forces that — adding a provider to
 * `VENUE_AMENITY_SOURCE_PROVIDERS` fails `pnpm type-check` here until it is
 * named, instead of silently shipping a mechanical uppercase of a slug.
 *
 * `openstreetmap` cannot reach a card today — the OSM lane writes
 * `venueFactProvenance.venueType`, and the API projects only the three boolean
 * columns — but it is in the stored vocabulary, so it is named here. **If a
 * future lane does stamp a boolean from OSM, this grid still needs its own
 * ODbL notice before that card ships**; the WEB-873 page-footer credit on
 * `/venues` and `/venue/[slug]` does not cover an amenity card. A provider
 * credit is not that notice.
 */
const PROVIDER_LABELS: Record<VenueAmenitySource['provider'], string> = {
  ticketmaster: 'TICKETMASTER',
  google_places: 'GOOGLE PLACES',
  openstreetmap: 'OPENSTREETMAP',
  venue_site: 'VENUE SITE',
};

/**
 * Render `{ provider, observedAt }` as the design's `TICKETMASTER · AUG 2026`.
 *
 * **Formatted in UTC, with a pinned `en-US` locale, on purpose.** `observedAt`
 * is a UTC instant and this component renders on the server and again in the
 * browser; resolving the month against the ambient zone would let those two
 * disagree across a month boundary and produce a hydration mismatch, and would
 * make the same stamp read as a different month for two readers. A provenance
 * stamp is coarse — the month is the point, not the hour — so a stable answer
 * beats a locally-correct one.
 *
 * Returns `null` for anything that is not an ISO-8601 instant, so the line
 * collapses. The API validates `observedAt` before serving it, but this
 * component also renders from fixtures and from cached bodies, and a half-built
 * stamp under a footnote promising confirmed facts is worse than no line.
 *
 * The shape is checked with a pattern, NOT with `Number.isNaN(date.getTime())`.
 * V8's `Date` constructor accepts a wide range of non-ISO strings by
 * implementation-defined fallback — `new Date('Aug 2026')` is a valid date, and
 * `new Date('2026-08-14')` is a date-only value pinned to UTC midnight — so a
 * parse check alone lets malformed input through and formats it as if it were a
 * real instant. Match the contract, then parse.
 */
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;

export function formatAmenitySource(source: VenueAmenitySource | null | undefined): string | null {
  if (!source) return null;

  const label = PROVIDER_LABELS[source.provider];
  if (!label) return null;

  if (!ISO_INSTANT.test(source.observedAt)) return null;

  const observedAt = new Date(source.observedAt);
  if (Number.isNaN(observedAt.getTime())) return null;

  const month = observedAt
    .toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })
    .toUpperCase();

  return `${label} · ${month}`;
}

function AmenityCard({ amenity }: { amenity: VenueAmenity }) {
  const sourceLine = formatAmenitySource(amenity.source);

  return (
    <div
      data-testid="venue-amenity-card"
      className="flex h-full flex-col gap-1 rounded-md border border-border/60 bg-card p-4"
    >
      {/*
       * WEB-861: `amenity.label` — the human-readable line. `amenity.value`
       * holds the raw provider token and is deliberately NOT rendered.
       */}
      <div
        data-testid="venue-amenity-value"
        className="text-sm font-semibold text-foreground font-display"
      >
        {amenity.label}
      </div>
      {hasSupplementaryFact(amenity.sub) ? (
        <div data-testid="venue-amenity-sub" className="text-micro text-muted-foreground">
          {amenity.sub}
        </div>
      ) : null}
      {/*
       * `.vn-amenity__src` (API-1709). `mt-auto` is load-bearing, not spacing:
       * grid items stretch to the tallest card in the row, so pushing the line
       * to the bottom is what makes the source lines align ACROSS a row even
       * when one card carries a sub-line and its neighbours do not. `pt-2`
       * keeps a floor under it when the card has no slack to distribute.
       *
       * The dot is decorative and `aria-hidden` — the separator is already
       * spoken by the `·` in the text, and a screen reader announcing a bullet
       * glyph adds nothing.
       */}
      {sourceLine ? (
        <div
          data-testid="venue-amenity-src"
          className="mt-auto flex items-center gap-1.5 pt-2 text-micro uppercase tracking-[0.08em] text-muted-foreground font-mono"
        >
          <span aria-hidden="true" className="h-[5px] w-[5px] shrink-0 rounded-full bg-ds-accent" />
          {sourceLine}
        </div>
      ) : null}
    </div>
  );
}

/**
 * COR-59 empty state (`vn-empty`). Nothing has been confirmed for this venue,
 * which means *unknown* — not "no parking", not "not accessible".
 *
 * The copy deliberately says "nothing is confirmed" rather than the design's
 * "haven't been checked". `normalizeAmenities` pushes an entry only for a
 * `true` boolean, so a confirmed `false` and an unknown NULL are indistinguishable
 * by the time they reach this component — an empty array cannot tell us whether
 * anyone looked. Claiming step-free access "hasn't been checked" for a venue whose
 * source recorded `false` would be a fabricated provenance claim on exactly the
 * accessibility surface COR-59 exists to protect.
 */
function AmenitiesUnverified() {
  return (
    <div
      data-testid="venue-amenity-unverified"
      className="grid grid-cols-[auto_1fr] items-start gap-4 rounded-md border border-dashed border-border bg-bg-sunken p-5"
    >
      <div
        aria-hidden="true"
        className="grid h-9 w-9 place-items-center rounded-full border border-border text-xs text-fg-3 font-mono"
      >
        ?
      </div>
      <div>
        <strong className="mb-1 block text-base font-semibold text-foreground font-display">
          Not verified yet
        </strong>
        <p className="m-0 max-w-[60ch] text-sm text-fg-2">
          We only list what a source has confirmed, and nothing is confirmed for this place yet.
          That means unknown, not no. Call ahead if parking, transit or step-free access matters for
          your night.
        </p>
      </div>
    </div>
  );
}

export function VenueAmenityGridView({
  amenities,
  className,
}: VenueAmenityGridViewProps): React.ReactElement {
  const headingId = React.useId();
  const hasAmenities = amenities.length > 0;

  return (
    <section
      role="region"
      aria-labelledby={headingId}
      data-testid="venue-amenity-grid"
      className={cn('mb-8 border-b border-border/60 pb-8', className)}
    >
      <h2
        id={headingId}
        className="m-0 mb-4 text-2xl font-bold tracking-tight text-foreground font-display sm:text-3xl"
      >
        Amenities
      </h2>
      {hasAmenities ? (
        <>
          <div className="grid grid-cols-2 gap-3 min-[700px]:grid-cols-3">
            {amenities.map((amenity, index) => (
              /*
               * Index-qualified, defensively. API-1709 made the API suppress the
               * duplicate at source — a `"parking"` token alongside
               * `hasParking = true` is now dropped, because rendering an
               * unstamped twin beside a stamped card asserted a second,
               * unverified claim about the same venue. This component still
               * accepts an arbitrary array (fixtures, a stale cached body), so
               * `label` alone remains an unsafe key.
               */
              <AmenityCard key={`${amenity.label}-${index}`} amenity={amenity} />
            ))}
          </div>
          <p
            data-testid="venue-amenity-foot"
            className="mt-4 max-w-[65ch] text-xs text-muted-foreground"
          >
            Only source-confirmed facts are listed. Anything missing here is unverified, not absent.
          </p>
        </>
      ) : (
        <AmenitiesUnverified />
      )}
    </section>
  );
}

export interface VenueAmenityGridProps {
  amenities: ReadonlyArray<VenueAmenity>;
  className?: string;
}

export function VenueAmenityGrid(props: VenueAmenityGridProps): React.ReactElement {
  return <VenueAmenityGridView {...props} />;
}
